import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import {
    Document, Packer, Paragraph, TextRun, ImageRun, Table, TableRow, TableCell,
    WidthType, PageBreak, Header, Footer, AlignmentType, PageNumber,
    PageOrientation, BorderStyle, VerticalAlign, TableLayoutType
} from 'docx';
import html2canvas from 'html2canvas';
import * as RO from './ReportObjects';
import * as G from './General';

// Word export mirrors the PDF composition model: the same ordered .report_row list,
// with the map and plots already flattened to <img> data-URLs by generate_pdf's conversion
// step. Tables, headings and text become native (editable) Word content; only genuine
// graphics are embedded as images.

// docx swaps width/height itself when orientation is LANDSCAPE, so dimensions are
// always given in portrait order.
const TWIPS_A4_LONG = 16838;
const TWIPS_A4_SHORT = 11906;
const MARGIN_TWIPS = 720;

const CHROME_FONT = 'Arial';
const TABLE_FONT = 'Courier New';
const TITLE_BLUE = '4E81BD'; // matches the PDF's table-title blue (78, 129, 189)

const NO_BORDER = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' };
const NO_BORDERS = { top: NO_BORDER, bottom: NO_BORDER, left: NO_BORDER, right: NO_BORDER };
const NO_BORDERS_ALL = { ...NO_BORDERS, insideHorizontal: NO_BORDER, insideVertical: NO_BORDER };

let content_width_twips = TWIPS_A4_LONG - 2 * MARGIN_TWIPS;
let content_width_px = 1020;

export async function generate_word(source) {
    try {
        const is_portrait = G.getPagePortrait();
        content_width_twips = (is_portrait ? TWIPS_A4_SHORT : TWIPS_A4_LONG) - 2 * MARGIN_TWIPS;
        content_width_px = is_portrait ? 700 : 1020;

        const host_logo = await loadImage(RO.host_logo, 40);
        const co_brand_src = RO.coBrandLogos.find(item => item[0] === RO.parent_uuid)?.[1] || null;
        const co_brand_logo = co_brand_src ? await loadImage(co_brand_src, 40) : null;

        const children = [];
        let last_was_break = false;
        for (const row of source) {
            if (row.classList.contains('page_break') || row.querySelector('.page_break')) {
                children.push(new Paragraph({ children: [new PageBreak()] }));
                last_was_break = true;
                continue;
            }

            const details_table = row.querySelector('.detailed_class');
            if (details_table) {
                // The detailed analysis always starts on a fresh page, title first — same as the PDF
                if (!last_was_break) children.push(new Paragraph({ children: [new PageBreak()] }));
                last_was_break = false;
                const sub_title = row.querySelector('.sub_title');
                if (sub_title) children.push(titleParagraph(sub_title.innerText, 32));
                children.push(htmlTableToDocx(details_table, {
                    risk_index: RO.risk_index, font_half_points: 13, width_twips: content_width_twips, centred: true
                }));
                continue;
            }

            const blocks = await serialiseChildren(row, content_width_twips, content_width_px);
            children.push(...blocks);
            if (blocks.length > 0) last_was_break = false;
        }

        // Blank notes page for the reader's own annotations
        children.push(new Paragraph({ children: [new PageBreak()] }));
        children.push(titleParagraph('Notes', 32));
        for (let i = 0; i < 12; i++) children.push(new Paragraph({ text: '' }));

        const doc_word = new Document({
            sections: [{
                properties: {
                    page: {
                        size: {
                            width: TWIPS_A4_SHORT,
                            height: TWIPS_A4_LONG,
                            orientation: is_portrait ? PageOrientation.PORTRAIT : PageOrientation.LANDSCAPE
                        },
                        margin: { top: MARGIN_TWIPS, bottom: MARGIN_TWIPS, left: MARGIN_TWIPS, right: MARGIN_TWIPS }
                    }
                },
                headers: { default: buildHeader(host_logo, co_brand_logo) },
                footers: { default: buildFooter(host_logo, co_brand_logo) },
                children: children
            }]
        });

        let blob = await Packer.toBlob(doc_word);
        try {
            // The body stays freely editable so recipients can amend the report; only the
            // branding chrome (header/footer) is locked, via content controls.
            blob = await applyChromeProtection(blob);
        } catch (error) {
            console.error('Word export: chrome protection failed, saving unprotected', error);
        }
        saveAs(blob, buildSaveName() + '.docx');
    } catch (error) {
        console.error('Word export failed:', error);
    } finally {
        const downloading = document.getElementById('downloading');
        if (downloading) downloading.style.display = 'none';
        const counter = document.getElementById('page_gen_counter');
        if (counter) counter.innerText = '';
    }
}

// ---------- DOM serialisation ----------

// Walks a report row and emits native docx blocks. Screen furniture (buttons, inputs,
// customisation containers) is skipped rather than rasterised.
async function serialiseChildren(element, width_twips, width_px) {
    const blocks = [];
    for (const node of element.childNodes) {
        if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent.trim();
            if (text) blocks.push(new Paragraph({ children: [new TextRun({ text, font: CHROME_FONT, size: 20 })] }));
            continue;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        if (isHidden(node)) continue;
        if (node.matches('button, input, select, .user_custom_container, .map_custom_cont, #map_custom_cont')) continue;

        if (node.tagName === 'TABLE') {
            blocks.push(htmlTableToDocx(node, { font_half_points: 18, width_twips: width_twips }));
            blocks.push(new Paragraph({ text: '' }));
        }
        else if (node.tagName === 'IMG') {
            const image_paragraph = await imageParagraphFromImg(node, width_px);
            if (image_paragraph) blocks.push(image_paragraph);
        }
        else if (node.matches('h1, h2, h3')) {
            blocks.push(titleParagraph(node.innerText, 44, '000000'));
        }
        else if (node.matches('p')) {
            const is_sub_title = node.classList.contains('sub_title');
            blocks.push(is_sub_title
                ? titleParagraph(node.innerText, 28)
                : new Paragraph({ children: runsFromNode(node, 20), spacing: { after: 60 } }));
        }
        else if (node.matches('.js-plotly-plot') || node.querySelector(':scope > canvas')) {
            const image_paragraph = await rasteriseToParagraph(node, width_px);
            if (image_paragraph) blocks.push(image_paragraph);
        }
        else if (node.id === 'map_logo_bar') {
            const strip = await mapLogoBarTable(node, width_twips);
            if (strip) blocks.push(strip);
        }
        else if (isSplitContainer(node)) {
            blocks.push(await twoColumnTable(node, width_twips, width_px));
        }
        else {
            blocks.push(...await serialiseChildren(node, width_twips, width_px));
        }
    }
    return blocks;
}

// The report lays halves out with .left/.right floats; Word gets a borderless two-column table
function isSplitContainer(element) {
    let left = null, right = null;
    for (const child of element.children) {
        if (child.classList.contains('left')) left = child;
        if (child.classList.contains('right')) right = child;
    }
    return left && right ? { left, right } : null;
}

async function twoColumnTable(element, width_twips, width_px) {
    const { left, right } = isSplitContainer(element);
    const half_twips = Math.floor(width_twips / 2);
    const half_px = Math.floor(width_px / 2) - 10;

    const cell = async (half) => new TableCell({
        borders: NO_BORDERS,
        width: { size: half_twips, type: WidthType.DXA },
        margins: { top: 40, bottom: 40, left: 60, right: 60 },
        children: ensureNotEmpty(await serialiseChildren(half, half_twips - 200, half_px))
    });

    return new Table({
        width: { size: width_twips, type: WidthType.DXA },
        columnWidths: [half_twips, half_twips],
        layout: TableLayoutType.FIXED,
        borders: NO_BORDERS_ALL,
        rows: [new TableRow({ children: [await cell(left), await cell(right)] })]
    });
}

function ensureNotEmpty(blocks) {
    return blocks.length > 0 ? blocks : [new Paragraph({ text: '' })];
}

// The strip under the map: brand logo bottom-left, host URL bottom-right (mirrors #map_logo_bar's floats)
async function mapLogoBarTable(element, width_twips) {
    const half = Math.floor(width_twips / 2);
    const left_runs = [];
    try {
        const img = element.querySelector('img');
        if (img && img.naturalWidth && img.naturalHeight) {
            const height = 40;
            const width = Math.round((img.naturalWidth / img.naturalHeight) * height);
            const bytes = await fetch(img.src).then((response) => response.arrayBuffer());
            left_runs.push(new ImageRun({ data: bytes, transformation: { width, height } }));
        }
    } catch (error) {
        // branding strip only — never block the export on a logo
    }
    const link_text = (element.innerText || '').trim();

    const cell = (runs, alignment) => new TableCell({
        borders: NO_BORDERS,
        verticalAlign: VerticalAlign.CENTER,
        width: { size: half, type: WidthType.DXA },
        children: [new Paragraph({ alignment, children: runs })]
    });

    return new Table({
        width: { size: width_twips, type: WidthType.DXA },
        columnWidths: [half, half],
        layout: TableLayoutType.FIXED,
        borders: NO_BORDERS_ALL,
        rows: [new TableRow({
            children: [
                cell(left_runs, AlignmentType.LEFT),
                cell([new TextRun({ text: link_text, font: CHROME_FONT, size: 18 })], AlignmentType.RIGHT)
            ]
        })]
    });
}

// ---------- tables ----------

function htmlTableToDocx(table_element, { risk_index = -1, font_half_points = 14, width_twips, centred = false } = {}) {
    const rows = [];
    for (const html_row of table_element.rows) {
        const cells = [];
        for (const html_cell of html_row.cells) {
            const is_header = html_cell.tagName === 'TH';
            let text = (html_cell.innerText || '').trim();

            // The PDF stamps a dot icon over 'X' marks in the MARPOL columns; a bullet is the text equivalent
            const cell_index = html_cell.cellIndex;
            if (risk_index >= 0 && !is_header && cell_index >= 11 && cell_index <= 15 && text === 'X') text = '●';

            let fill = is_header ? '2980B9' : undefined;
            let colour = is_header ? 'FFFFFF' : '000000';
            if (!is_header && cell_index === risk_index) {
                const [bg_hex, text_rgb] = RO.color_mappings[text.toUpperCase()] || ['', [0, 0, 0]];
                if (bg_hex !== '') fill = bg_hex.replace('#', '');
                colour = rgbToHex(text_rgb);
            }

            const runs = (risk_index >= 0 || is_header)
                ? [new TextRun({ text, bold: is_header, font: TABLE_FONT, size: font_half_points, color: colour })]
                : runsFromNode(html_cell, font_half_points, colour);

            cells.push(new TableCell({
                columnSpan: html_cell.colSpan > 1 ? html_cell.colSpan : undefined,
                verticalAlign: VerticalAlign.CENTER,
                shading: fill ? { fill: fill } : undefined,
                margins: { top: 40, bottom: 40, left: 60, right: 60 },
                children: [new Paragraph({
                    alignment: centred ? AlignmentType.CENTER : AlignmentType.LEFT,
                    children: runs
                })]
            }));
        }
        if (cells.length > 0) rows.push(new TableRow({ children: cells, tableHeader: html_row.parentElement.tagName === 'THEAD' }));
    }
    return new Table({
        width: { size: width_twips, type: WidthType.DXA },
        columnWidths: measureColumnWidths(table_element, width_twips),
        layout: TableLayoutType.FIXED,
        rows: rows
    });
}

// Word needs an explicit column grid or strict renderers shrink-wrap the table;
// the on-screen table already has the right proportions, so scale those into twips.
function measureColumnWidths(table_element, width_twips) {
    let column_count = 0;
    for (const html_row of table_element.rows) {
        let count = 0;
        for (const html_cell of html_row.cells) count += html_cell.colSpan || 1;
        column_count = Math.max(column_count, count);
    }
    if (column_count === 0) return [width_twips];

    // Use a span-free row as the measuring stick when one exists
    const measure_row = Array.from(table_element.rows).find((html_row) =>
        html_row.cells.length === column_count && Array.from(html_row.cells).every((html_cell) => (html_cell.colSpan || 1) === 1)
    );
    if (measure_row) {
        const px_widths = Array.from(measure_row.cells).map((html_cell) => Math.max(html_cell.offsetWidth, 1));
        const total_px = px_widths.reduce((sum, value) => sum + value, 0);
        if (total_px > 0) return px_widths.map((value) => Math.max(Math.round((value / total_px) * width_twips), 200));
    }
    return new Array(column_count).fill(Math.floor(width_twips / column_count));
}

// Preserves the report's "<span class='title'>Label</span><br>value" cell pattern as bold label + line break + value
function runsFromNode(node, size_half_points, colour = '000000') {
    const runs = [];
    let pending_break = false;
    const walk = (parent, bold) => {
        for (const child of parent.childNodes) {
            if (child.nodeType === Node.TEXT_NODE) {
                const text = child.textContent.replace(/\s+/g, ' ').trim();
                if (!text) continue;
                runs.push(new TextRun({
                    text, bold,
                    font: CHROME_FONT, size: size_half_points, color: colour,
                    break: pending_break ? 1 : undefined
                }));
                pending_break = false;
            } else if (child.nodeType === Node.ELEMENT_NODE) {
                if (child.tagName === 'BR') { pending_break = true; continue; }
                walk(child, bold || child.classList.contains('title') || child.tagName === 'B' || child.tagName === 'STRONG');
            }
        }
    };
    walk(node, false);
    return runs.length > 0 ? runs : [new TextRun({ text: '', size: size_half_points })];
}

// ---------- images ----------

async function imageParagraphFromImg(img_element, target_width_px) {
    try {
        const natural_width = img_element.naturalWidth || img_element.width;
        const natural_height = img_element.naturalHeight || img_element.height;
        // Placeholder pixels (1x1 temp images) render as empty picture frames in Word
        if (!natural_width || !natural_height || natural_width < 8 || natural_height < 8) return null;
        const width = Math.min(target_width_px, natural_width);
        const height = Math.round((natural_height / natural_width) * width);
        const bytes = await fetch(img_element.src).then((response) => response.arrayBuffer());
        return new Paragraph({ children: [new ImageRun({ data: bytes, transformation: { width, height } })] });
    } catch (error) {
        console.error('Word export: failed to embed image', error);
        return null;
    }
}

// Last-resort fallback for live plot elements that were not pre-converted to <img>
async function rasteriseToParagraph(element, target_width_px) {
    try {
        const canvas = await html2canvas(element, { logging: false, scale: 2 });
        if (canvas.width === 0 || canvas.height === 0) return null;
        const width = Math.min(target_width_px, element.offsetWidth);
        const height = Math.round((canvas.height / canvas.width) * width);
        const bytes = await fetch(canvas.toDataURL('image/jpeg', 0.92)).then((response) => response.arrayBuffer());
        return new Paragraph({ children: [new ImageRun({ data: bytes, transformation: { width, height } })] });
    } catch (error) {
        console.error('Word export: failed to rasterise element', error);
        return null;
    }
}

// ---------- chrome ----------

function buildHeader(host_logo, co_brand_logo) {
    return new Header({
        children: [chromeRow(
            [new TextRun({ text: RO.report_name, font: CHROME_FONT, size: 44 })],
            [],
            logoRuns(host_logo, co_brand_logo)
        )]
    });
}

function buildFooter(host_logo, co_brand_logo) {
    const user = G.getSessionUser();
    const parent = G.getSessionParent();
    const generated_by = (user && user.email)
        ? 'Generated by ' + user.email.trim() + (parent && parent.parent_account_name ? ' (' + parent.parent_account_name + ')' : '')
        : '';
    const date_text = new Date().toLocaleString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: 'numeric', timeZone: 'UTC'
    }) + ' (UTC)';

    return new Footer({
        children: [chromeRow(
            [
                new TextRun({ text: date_text, font: CHROME_FONT, size: 18 }),
                new TextRun({ text: 'Page ', font: CHROME_FONT, size: 18, break: 1 }),
                new TextRun({ children: [PageNumber.CURRENT], font: CHROME_FONT, size: 18 })
            ],
            [
                new TextRun({ text: generated_by, font: CHROME_FONT, size: 18 }),
                new TextRun({ text: RO.host_url, font: CHROME_FONT, size: 18, break: 1 })
            ],
            logoRuns(host_logo, co_brand_logo)
        )]
    });
}

// One borderless three-cell table: the same left / centre / right chrome layout the PDF uses
function chromeRow(left_runs, centre_runs, right_runs) {
    const third = Math.floor(content_width_twips / 3);
    const cell = (runs, alignment) => new TableCell({
        borders: NO_BORDERS,
        verticalAlign: VerticalAlign.CENTER,
        width: { size: third, type: WidthType.DXA },
        children: [new Paragraph({ alignment, children: runs })]
    });
    return new Table({
        width: { size: content_width_twips, type: WidthType.DXA },
        columnWidths: [third, third, third],
        layout: TableLayoutType.FIXED,
        borders: NO_BORDERS_ALL,
        rows: [new TableRow({
            children: [
                cell(left_runs, AlignmentType.LEFT),
                cell(centre_runs, AlignmentType.CENTER),
                cell(right_runs, AlignmentType.RIGHT)
            ]
        })]
    });
}

function logoRuns(host_logo, co_brand_logo) {
    const runs = [];
    if (co_brand_logo) {
        runs.push(imageRun(co_brand_logo));
        runs.push(new TextRun({ text: '   ' }));
    }
    if (host_logo) runs.push(imageRun(host_logo));
    return runs;
}

function titleParagraph(title, size_half_points, colour = TITLE_BLUE) {
    return new Paragraph({
        children: [new TextRun({ text: title, bold: true, font: CHROME_FONT, size: size_half_points, color: colour })],
        spacing: { before: 120, after: 120 }
    });
}

function imageRun(image) {
    return new ImageRun({ data: image.bytes, transformation: { width: image.width, height: image.height } });
}

// ---------- helpers ----------

function isHidden(element) {
    const style = getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') return true;
    return element.offsetWidth === 0 && element.offsetHeight === 0;
}

function buildSaveName() {
    let save_name = '';
    if (RO.report_type === '2') save_name = 'Single_Incident_Report-';
    if (RO.report_type === '3') save_name = 'Vessel_Data_Sheet-';
    if (RO.report_type === '4') save_name = 'Timeline_Report-';
    const title = document.getElementById('report_title')?.innerText || RO.report_name;
    return save_name + title.replace(/[^a-zA-Z0-9]/g, '_');
}

// Fetches an image and scales it to a target display height, mirroring the PDF logo sizing.
// Returns null on any failure — a missing white-label logo must never abort the export.
async function loadImage(src, target_height_px) {
    try {
        const bytes = await fetch(src).then((response) => response.arrayBuffer());
        const meta = await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = src;
        });
        const width = Math.round((target_height_px / meta.naturalHeight) * meta.naturalWidth);
        return { bytes: bytes, width: width, height: target_height_px };
    } catch (error) {
        console.error('Word export: logo failed to load', src, error);
        return null;
    }
}

function rgbToHex(rgb) {
    if (!Array.isArray(rgb)) return '000000';
    return rgb.map((value) => value.toString(16).padStart(2, '0')).join('').toUpperCase();
}

// ---------- chrome protection ----------
// Locks only the header and footer by wrapping each one's content in a content control with
// sdtContentLocked. There is no document-wide protection, so the body stays freely editable
// and Word shows none of the yellow "editable region" shading. This deters casual edits to
// the branding chrome; a determined user can still unlock it via Word's Developer tab.

async function applyChromeProtection(blob) {
    const zip = await JSZip.loadAsync(blob);
    let sdt_id = 900001;

    for (const part of Object.keys(zip.files)) {
        const match = part.match(/^word\/(header|footer)\d+\.xml$/);
        if (!match) continue;

        const tag = match[1] === 'header' ? 'w:hdr' : 'w:ftr';
        const xml = await zip.file(part).async('string');
        const open = xml.match(new RegExp('<' + tag + '\\b[^>]*>'));
        const close_index = xml.lastIndexOf('</' + tag + '>');
        if (!open || close_index < 0) continue;

        const open_end = open.index + open[0].length;
        const inner = xml.slice(open_end, close_index);
        if (!inner.trim()) continue;

        const locked = '<w:sdt><w:sdtPr><w:id w:val="' + (sdt_id++) + '"/>'
            + '<w:lock w:val="sdtContentLocked"/></w:sdtPr>'
            + '<w:sdtContent>' + inner + '</w:sdtContent></w:sdt>';
        zip.file(part, xml.slice(0, open_end) + locked + xml.slice(close_index));
    }

    return zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 },
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    });
}
